import { database } from '@app/firebase';
import useCollection from '@hooks/useCollection';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { highestOffer } from '@ui/components/product-offer/ProductOffer';
import { offerPrice } from '@ui/utils/offerPrice';
import { collection as firestoreCollection, limit as firestoreLimit, query as firestoreQuery } from 'firebase/firestore';
import Image, { ImageProps } from 'next/image';
import React, { CSSProperties, useMemo } from 'react';

import useStyles from './ProductCard.style';

export interface ProductCardProps {
  product?: Partial<guesthouse.Product>;
  imageHeight?: string | number;
  imageWidth?: string | number;
  style?: CSSProperties;
  className?: string;
  saved?: boolean;
  tabIndex?: number;
  enableQuickView?: boolean;
  showMaker?: boolean;
  showDiscount?: boolean;
  offer?: guesthouse.ProductOffer;
  onSavedClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onClick?: (product: Partial<guesthouse.Product>) => void;
  renderCta?: (props?: ProductCardProps) => React.ReactElement;
  ImageProps?: Partial<ImageProps>;
  useLink?: boolean;
  asyncDialog?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = (props) => {
  const {
    product,
    imageHeight,
    imageWidth,
    style,
    className,
    onClick,
    onSavedClick,
    tabIndex,
    ImageProps,
    offer,
  } = props;

  const { classes, cx } = useStyles({});
  const lowestOffer = highestOffer(product.offers);
  const { percentDiscount } = offerPrice(offer || lowestOffer);

  const styleObj: CSSProperties = { ...style };

  if (typeof onClick === 'function') {
    styleObj.cursor = 'pointer';
  }

  const query = firestoreQuery(firestoreCollection(database, `products/${product.docID}/photos`), firestoreLimit(1));
  const { collection } = useCollection(query);
  const docs = collection && collection.docs;

  const photo = useMemo(() => {
    if (product.primaryPhoto) {
      return product.primaryPhoto;
    } else if (docs && docs[0]) {
      return docs[0].data() as guesthouse.Photo;
    }
  }, [docs, product.primaryPhoto]);

  return (
    <Box
      className={cx({
        [classes.cardContainer]: true,
        [className]: !!className,
      })}
      style={styleObj}
      onClick={() => typeof onClick === 'function' && onClick(product)}
    >
      <div
        tabIndex={tabIndex}
      >
        <div
          className={classes.imageContainer}
          style={{ height: imageHeight ? imageHeight : undefined, width: imageWidth ? imageWidth : undefined }}
        >
          <Image
            src={photo?.medium_url ?? '/images/misc/product-default.png'}
            alt=""
            {...ImageProps}
            fill
            sizes="100vw"
            style={{
              objectFit: 'contain'
            }}
          />
        </div>
        {percentDiscount > 0 && (
          <div
            className={classes.salePercent}
          >
            {`${percentDiscount}% Off`}
          </div>
        )}
      </div>
      <Box
        className={classes.infoContainer}
      >

        <Typography
          component="div"
          className={cx({
            [classes.productTitle]: true,
            [classes.withSavedPadding]: Boolean(onSavedClick),
            [classes.withSavedProductTitle]: Boolean(onSavedClick),
          })}
        >
          {product.title}
        </Typography>
      </Box>
    </Box>
  );
};

export default ProductCard;
